import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../src/lib/supabase';

// --- Icons ---
const UploadIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
);

const ImageIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="18" height="18" x="3" y="3" rx="2" ry="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" /></svg>
);

const LinkIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
);

const XIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></svg>
);

// --- Component ---

interface ImageLibraryProps {
    onSelect: (url: string) => void;
    onClose: () => void;
    initialUrl?: string;
}

interface StoredImage {
    name: string;
    url: string;
    created_at: string;
}

export const ImageLibrary: React.FC<ImageLibraryProps> = ({ onSelect, onClose, initialUrl }) => {
    const [activeTab, setActiveTab] = useState<'upload' | 'library' | 'url'>('upload');
    const [images, setImages] = useState<StoredImage[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [userId, setUserId] = useState<string | null>(null);
    const [externalUrl, setExternalUrl] = useState(initialUrl && initialUrl.startsWith('http') ? initialUrl : '');
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        supabase.auth.getUser().then(({ data }) => {
            if (data.user) {
                setUserId(data.user.id);
                fetchImages(data.user.id);
            }
        });
    }, []);

    const fetchImages = async (uid: string) => {
        setLoading(true);
        const { data, error } = await supabase.storage.from('user_uploads').list(uid, {
            limit: 100,
            offset: 0,
            sortBy: { column: 'created_at', order: 'desc' },
        });

        if (error) {
            console.error('Error fetching images:', error);
        } else {
            const imageList = data.map(file => {
                const { data: urlData } = supabase.storage.from('user_uploads').getPublicUrl(`${uid}/${file.name}`);
                return {
                    name: file.name,
                    url: urlData.publicUrl,
                    created_at: file.created_at,
                };
            });
            setImages(imageList);
            if (imageList.length > 0) {
                setActiveTab('library');
            }
        }
        setLoading(false);
    };

    const handleUpload = async (files: FileList | null) => {
        if (!files || files.length === 0 || !userId) return;

        setUploading(true);
        const file = files[0];
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${userId}/${fileName}`;

        const { error } = await supabase.storage.from('user_uploads').upload(filePath, file);

        if (error) {
            alert('Erro ao fazer upload: ' + error.message);
        } else {
            await fetchImages(userId);
            setActiveTab('library');
        }
        setUploading(false);
    };

    const handleDelete = async (imageName: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!userId || !confirm('Ter certeza que deseja excluir esta imagem?')) return;

        const { error } = await supabase.storage.from('user_uploads').remove([`${userId}/${imageName}`]);
        if (error) {
            alert('Erro ao excluir: ' + error.message);
        } else {
            setImages(images.filter(img => img.name !== imageName));
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleUpload(e.dataTransfer.files);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-lg font-semibold text-slate-800">Biblioteca de Imagens</h2>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-100 text-slate-500 hover:text-red-500 transition-colors">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b bg-slate-50">
                    <button
                        onClick={() => setActiveTab('upload')}
                        className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'upload' ? 'bg-white text-sky-600 border-t-2 border-t-sky-600' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                        <UploadIcon className="w-4 h-4" /> Upload
                    </button>
                    <button
                        onClick={() => setActiveTab('library')}
                        className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'library' ? 'bg-white text-sky-600 border-t-2 border-t-sky-600' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                        <ImageIcon className="w-4 h-4" /> Minha Galeria
                    </button>
                    <button
                        onClick={() => setActiveTab('url')}
                        className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'url' ? 'bg-white text-sky-600 border-t-2 border-t-sky-600' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                        <LinkIcon className="w-4 h-4" /> URL Externa
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 bg-slate-50">

                    {/* Upload Tab */}
                    {activeTab === 'upload' && (
                        <div className="h-full flex flex-col items-center justify-center">
                            <div
                                className={`w-full max-w-sm border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center transition-all cursor-pointer ${dragActive ? 'border-sky-500 bg-sky-50' : 'border-slate-300 hover:border-slate-400 bg-white'}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="hidden"
                                    accept="image/*"
                                    onChange={(e) => handleUpload(e.target.files)}
                                />
                                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4 text-slate-400">
                                    {uploading ? (
                                        <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <UploadIcon className="w-8 h-8" />
                                    )}
                                </div>
                                <p className="text-slate-700 font-medium mb-1">{uploading ? 'Enviando...' : 'Clique para selecionar ou arraste aqui'}</p>
                                <p className="text-xs text-slate-500">Suporta JPG, PNG, GIF, WEBP</p>
                            </div>
                        </div>
                    )}

                    {/* Library Tab */}
                    {activeTab === 'library' && (
                        <div className="h-full">
                            {loading ? (
                                <div className="flex h-full items-center justify-center">
                                    <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            ) : images.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-slate-400">
                                    <ImageIcon className="w-12 h-12 mb-2 opacity-20" />
                                    <p>Nenhuma imagem encontrada</p>
                                    <button onClick={() => setActiveTab('upload')} className="mt-2 text-sky-600 text-sm hover:underline"> Fazer upload </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                    {images.map((img) => (
                                        <div
                                            key={img.name}
                                            className="group relative aspect-square bg-white border rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-sky-500 transition-all shadow-sm hover:shadow-md"
                                            onClick={() => onSelect(img.url)}
                                        >
                                            <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                                            <button
                                                onClick={(e) => handleDelete(img.name, e)}
                                                className="absolute top-2 right-2 p-1.5 bg-white/90 text-red-500 rounded-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                                                title="Excluir imagem"
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* URL Tab */}
                    {activeTab === 'url' && (
                        <div className="h-full flex flex-col items-center justify-center max-w-md mx-auto">
                            <label className="block w-full text-sm font-medium text-slate-700 mb-2">URL da Imagem</label>
                            <div className="flex w-full gap-2">
                                <input
                                    type="text"
                                    value={externalUrl}
                                    onChange={(e) => setExternalUrl(e.target.value)}
                                    className="flex-1 px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
                                    placeholder="https://exemplo.com/imagem.jpg"
                                />
                                <button
                                    onClick={() => onSelect(externalUrl)}
                                    disabled={!externalUrl}
                                    className="px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Usar
                                </button>
                            </div>
                            {externalUrl && (
                                <div className="mt-4 w-full aspect-video bg-slate-100 rounded-lg overflow-hidden border">
                                    <img src={externalUrl} alt="Preview" className="w-full h-full object-contain" onError={(e) => (e.currentTarget.style.display = 'none')} />
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};
